import React, { useEffect, useId, useRef, useState } from 'react'

import { ChartView } from './ChartView'
import { formatNumber } from './chartFormat'

/**
 * The NFL team lookup chart: any team's points per game, points allowed per
 * game or wins each regular season since 1999, with the league's points per
 * team per game as a dashed line. The page's own data (`chart.data`) is the
 * featured team's points per game; `chart.team` is the team in the address
 * (`?team=dallas-cowboys`) when the server already has it; the league average
 * is `chart.measures.average` (Chart#lookup_data); any other team comes from
 * GET /nfl-teams/:slug.json (NflTeamsController). The address follows each
 * choice, so a team's page can be shared and found.
 */

const FEATURED = { name: 'Kansas City Chiefs', slug: 'kansas-city-chiefs' } // keep in step with chart.yml
// Chart::Recipes::NflTeam::TEAMS
const TEAMS = [
  'Arizona Cardinals', 'Atlanta Falcons', 'Baltimore Ravens', 'Buffalo Bills', 'Carolina Panthers', 'Chicago Bears',
  'Cincinnati Bengals', 'Cleveland Browns', 'Dallas Cowboys', 'Denver Broncos', 'Detroit Lions', 'Green Bay Packers',
  'Houston Texans', 'Indianapolis Colts', 'Jacksonville Jaguars', 'Kansas City Chiefs', 'Las Vegas Raiders',
  'Los Angeles Chargers', 'Los Angeles Rams', 'Miami Dolphins', 'Minnesota Vikings', 'New England Patriots',
  'New Orleans Saints', 'New York Giants', 'New York Jets', 'Philadelphia Eagles', 'Pittsburgh Steelers',
  'San Francisco 49ers', 'Seattle Seahawks', 'Tampa Bay Buccaneers', 'Tennessee Titans', 'Washington Commanders',
].map((name) => ({ name, slug: name.toLowerCase().replace(/[^a-z0-9]+/g, '-') }))
const MEASURES = {
  points_per_game: { button: 'Points scored', yLabel: 'Points scored per game', title: 'Points scored per game', league: true },
  points_allowed: { button: 'Points allowed', yLabel: 'Points allowed per game', title: 'Points allowed per game', league: true },
  wins: { button: 'Wins', yLabel: 'Regular-season wins', title: 'Wins', league: false },
}

function setAddress(slug, measure) {
  const url = new URL(window.location.href)
  url.searchParams.set('team', slug)
  if (measure === 'points_per_game') url.searchParams.delete('measure')
  else url.searchParams.set('measure', measure)
  window.history.replaceState(null, '', url)
}

/** A measure's points from a team's seasons, or null when only the page's own points are known. */
export function measurePoints(result, measure) {
  if (result.seasons) return result.seasons.map((s) => ({ label: s.season, value: s[measure] }))
  return measure === 'points_per_game' ? result.data : null
}

export function NflTeamLookup({ chart }) {
  const selectId = useId()
  const [result, setResult] = useState(chart.team ?? { ...FEATURED, data: chart.data })
  const [measure, setMeasure] = useState(() => {
    const asked = typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get('measure')
    return MEASURES[asked] ? asked : 'points_per_game'
  })
  const [status, setStatus] = useState({ loading: false, message: null })
  const lookupRef = useRef(null)
  const league = chart.measures?.average ?? []

  const lookUp = async (slug, chosen = measure) => {
    lookupRef.current?.abort()
    const controller = new AbortController()
    lookupRef.current = controller
    setStatus({ loading: true, message: null })
    try {
      const response = await fetch(`/nfl-teams/${encodeURIComponent(slug)}.json`, { headers: { Accept: 'application/json' }, signal: controller.signal })
      if (!response.ok) throw new Error(`Request failed (${response.status})`)
      setResult(await response.json())
      setMeasure(chosen)
      setStatus({ loading: false, message: null })
      setAddress(slug, chosen)
    } catch (error) {
      if (error.name === 'AbortError') return
      setStatus({ loading: false, message: 'That team’s seasons couldn’t be loaded. Please try again.' })
    }
  }

  // Open the team (or measure) in the address, unless the server already sent it.
  useEffect(() => {
    const asked = new URLSearchParams(window.location.search).get('team')
    if (asked && asked !== result.slug && TEAMS.some((t) => t.slug === asked)) lookUp(asked)
    else if (!measurePoints(result, measure)) lookUp(result.slug)
    return () => lookupRef.current?.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const chooseMeasure = (value) => {
    if (!measurePoints(result, value)) {
      lookUp(result.slug, value) // the featured team arrives with its points per game only
      return
    }
    setMeasure(value)
    setAddress(result.slug, value)
  }

  const meta = MEASURES[measure]
  const points = measurePoints(result, measure) ?? []
  const leaguePoints = meta.league ? league : []
  const latest = points[points.length - 1]
  const latestSeason = result.seasons?.[result.seasons.length - 1]
  const leagueLatest = latest ? league.find((p) => p.label === latest.label) : null
  const leagueBySeason = new Map(league.map((p) => [p.label, p.value]))

  return (
    <div className="w-full">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0 sm:w-72">
          <label htmlFor={selectId} className="form-label">Choose a team</label>
          <select
            id={selectId}
            className="input-form-field min-h-10"
            value={result.slug}
            disabled={status.loading}
            onChange={(event) => lookUp(event.target.value)}
          >
            {TEAMS.map((t) => <option key={t.slug} value={t.slug}>{t.name}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Measure">
          {Object.entries(MEASURES).map(([value, { button }]) => (
            <button
              key={value}
              type="button"
              className={`${measure === value ? 'btn-primary' : 'btn-secondary'} btn-sm min-h-10`}
              aria-pressed={measure === value}
              disabled={status.loading}
              onClick={() => chooseMeasure(value)}
            >
              {button}
            </button>
          ))}
        </div>
      </div>

      {status.message ? <p className="alert-info mb-4" role="status">{status.message}</p> : null}

      <p className="mb-4 font-semibold text-ink break-words" aria-live="polite">
        {status.loading ? 'Loading…' : result.name}
        {latestSeason ? (
          <span className="block text-sm font-normal text-ink-muted">
            {latestSeason.season}: {latestSeason.wins}–{latestSeason.losses}{latestSeason.ties ? `–${latestSeason.ties}` : ''},
            {' '}{formatNumber(latestSeason.points_per_game)} points scored and {formatNumber(latestSeason.points_allowed)} allowed a game
            {leagueLatest ? ` (NFL average: ${formatNumber(leagueLatest.value)})` : ''}.
          </span>
        ) : latest ? (
          <span className="block text-sm font-normal text-ink-muted">
            {latest.label}: {formatNumber(latest.value)} points scored a game
            {leagueLatest ? ` (NFL average: ${formatNumber(leagueLatest.value)})` : ''}.
          </span>
        ) : null}
      </p>

      <ChartView
        searchable={false}
        chart={{
          ...chart,
          title: `${meta.title} by the ${result.name}, each regular season`,
          y_label: meta.yLabel,
          data: points,
          series_name: result.name,
          reference: leaguePoints.length ? { name: 'NFL average', data: leaguePoints } : null,
        }}
      />
      <p className="mt-2 text-xs text-ink-muted">
        Regular seasons only. Seasons had 16 games until 2021, then 17{measure === 'wins' ? '; a tie isn’t a win' : ''}.
        {meta.league ? ' The dashed line is the average for all teams, which scored and allowed the same points.' : ''}
      </p>

      {result.seasons?.length ? (
        <details className="mt-4 text-sm">
          <summary className="cursor-pointer font-semibold text-primary">The numbers for the {result.name}</summary>
          <div className="mt-2 max-h-80 overflow-y-auto">
            <table className="admin-table w-full">
              <thead>
                <tr>
                  <th scope="col" className="text-left">Season</th>
                  <th scope="col" className="text-right">Record</th>
                  <th scope="col" className="text-right">Scored</th>
                  <th scope="col" className="text-right">Allowed</th>
                  <th scope="col" className="text-right">NFL average</th>
                </tr>
              </thead>
              <tbody>
                {[...result.seasons].reverse().map((s) => (
                  <tr key={s.season}>
                    <td>
                      {s.season}
                      {s.name !== result.name ? <span className="block text-xs text-ink-muted">{s.name}</span> : null}
                    </td>
                    <td className="text-right tabular-nums">{s.wins}–{s.losses}{s.ties ? `–${s.ties}` : ''}</td>
                    <td className="text-right tabular-nums">{s.points_per_game}</td>
                    <td className="text-right tabular-nums">{s.points_allowed}</td>
                    <td className="text-right tabular-nums">{leagueBySeason.get(s.season) ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-ink-muted">Records are wins–losses(–ties); points are per game, from nflverse’s scores.</p>
          </div>
        </details>
      ) : null}
    </div>
  )
}
